import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import { TourCard } from '../discovery/GoalTourGuide';
import { setUiFlag } from '../../utils/uiFlags';
import { useTour, setTour, PLATFORM_STEPS } from './tourState';

/*
 * The platform walk - the tour's second act. After the goal-page tour
 * ("Continue tour") the user is taken across the workspace pages in
 * order: Signals -> Discovery -> Relationships -> Segments -> Models ->
 * Pipelines -> Trainings -> Deployments -> Inferences -> Data Sources
 * (which closes on "add any data, anytime").
 *
 * Mounted once at app level so the card survives navigation. Each card
 * explains the page the tour is on; Next navigates to the following
 * page. The sidebar pulses the current step's item (see Sidebar.js) so
 * users anchor page <-> nav. If the user wanders off the tour path the
 * card stays, and the primary button brings them back.
 *
 * One time ever, account-scoped: the goal tour already set
 * ui_flags.tour_done when it handed off, so an interrupted walk never
 * resurrects itself in a later session. Skip clears it immediately.
 */

const PlatformTourGuide = () => {
  const tour = useTour();
  const location = useLocation();
  const navigate = useNavigate();

  if (!tour || tour.phase !== 'platform') return null;
  const step = PLATFORM_STEPS[tour.idx];
  if (!step) { setTour(null); return null; }

  const onPage = location.pathname === step.path
    || location.pathname.startsWith(`${step.path}/`);
  const isLast = tour.idx === PLATFORM_STEPS.length - 1;
  const next = PLATFORM_STEPS[tour.idx + 1] || null;

  const finish = () => { setUiFlag('tour_done'); setTour(null); };
  const advance = () => {
    if (isLast) { finish(); return; }
    setTour({ phase: 'platform', idx: tour.idx + 1 });
    navigate(next.path);
  };

  return (
    <TourCard
      key={`${step.path}-${onPage ? 'on' : 'off'}`}
      title={step.title}
      text={onPage
        ? step.explain
        : `Next stop: ${step.label}. The pulsing item in the sidebar is where it lives.`}
      progress={{ idx: tour.idx, total: PLATFORM_STEPS.length }}
      onSkip={finish}
      action={onPage ? {
        label: isLast ? 'Finish' : `Next: ${next.label}`,
        icon: isLast
          ? <CheckRoundedIcon sx={{ fontSize: 15 }} />
          : <ArrowForwardIcon sx={{ fontSize: 15 }} />,
        onClick: advance,
      } : {
        label: 'Take me there',
        icon: <ArrowForwardIcon sx={{ fontSize: 15 }} />,
        onClick: () => navigate(step.path),
      }}
    />
  );
};

export default PlatformTourGuide;
